import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    MessageSquare,
    FileText,
    Smile,
    Mail,
    Bell,
    ShoppingCart,
    Calendar,
    CheckCircle,
    Send,
    Phone,
    Plus,
    X,
    Loader2,
    AlertCircle,
    Hash,
    ChevronRight,
    Copy
} from 'lucide-react';

// Icon mapping for displaying template icons
const LucideIcons = {
    FileText,
    Smile,
    MessageSquare,
    Mail,
    Bell,
    ShoppingCart,
    Calendar,
    CheckCircle,
    Send,
    Phone
};

const API = (path, opts, authFetch) => authFetch(`/api/admin${path}`, opts);

const TemplatesByRole = () => {
    const { authFetch } = useAuth();
    const [roles, setRoles] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleTemplates, setRoleTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load all roles
            const rolesRes = await API('/roles', {}, authFetch);
            const rolesData = await rolesRes.json();
            setRoles(rolesData.roles || []);

            // Load all templates
            const templatesRes = await API('/templates', {}, authFetch);
            const templatesData = await templatesRes.json();
            setTemplates(templatesData.templates || []);

            // Select first role by default
            if (rolesData.roles && rolesData.roles.length > 0) {
                setSelectedRole(rolesData.roles[0]);
            }
        } catch (err) {
            console.error('[TemplatesByRole] Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedRole) {
            loadRoleTemplates(selectedRole.id);
        }
    }, [selectedRole]);

    const loadRoleTemplates = async (roleId) => {
        try {
            const res = await API(`/roles/${roleId}/templates`, {}, authFetch);
            const data = await res.json();
            setRoleTemplates(data.templates || []);
        } catch (err) {
            console.error('[TemplatesByRole] Error loading role templates:', err);
        }
    };

    const assignTemplate = async (templateId) => {
        if (!selectedRole) return;
        setBusy(true);
        try {
            await API(`/roles/${selectedRole.id}/templates`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ template_id: templateId, display_order: roleTemplates.length })
            }, authFetch);
            await loadRoleTemplates(selectedRole.id);
        } catch (err) {
            console.error('[TemplatesByRole] Error assigning template:', err);
            alert('Error al asignar template');
        } finally {
            setBusy(false);
        }
    };

    const removeTemplate = async (templateId) => {
        if (!selectedRole) return;
        setBusy(true);
        try {
            await API(`/roles/${selectedRole.id}/templates/${templateId}`, { method: 'DELETE' }, authFetch);
            await loadRoleTemplates(selectedRole.id);
        } catch (err) {
            console.error('[TemplatesByRole] Error removing template:', err);
            alert('Error al desasignar template');
        } finally {
            setBusy(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const assignedTemplateIds = new Set(roleTemplates.map(t => t.id));
    const availableTemplates = templates.filter(t => !assignedTemplateIds.has(t.id) && t.is_active);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="h-screen overflow-hidden bg-[#020817] text-slate-50">
            {/* Header */}
            <div className="border-b border-slate-800 bg-[#0f172a] px-8 py-6">
                <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
                    <MessageSquare className="text-primary" size={28} />
                    Templates por Rol
                </h1>
                <p className="text-slate-400 mt-2">Gestiona qué templates de WhatsApp están disponibles para cada rol</p>
            </div>

            {/* Main Content - 2 columns */}
            <div className="grid grid-cols-12 gap-6 p-8 h-[calc(100vh-120px)] overflow-hidden">
                {/* Left Column - Roles List */}
                <div className="col-span-4 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden flex flex-col">
                    <div className="px-5 py-4 border-b border-slate-800">
                        <h2 className="font-semibold text-slate-200 flex items-center gap-2">
                            <Hash size={16} className="text-primary" />
                            Roles ({roles.length})
                        </h2>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {roles.length === 0 && (
                            <div className="p-6 text-center">
                                <AlertCircle size={24} className="text-slate-600 mx-auto mb-2" />
                                <p className="text-sm text-slate-500">No hay roles configurados</p>
                            </div>
                        )}
                        {roles.map(role => (
                            <button
                                key={role.id}
                                onClick={() => setSelectedRole(role)}
                                className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                                    selectedRole?.id === role.id
                                        ? 'bg-primary/10 border border-primary/20 text-slate-100'
                                        : 'bg-slate-800/50 border border-transparent text-slate-300 hover:bg-slate-800 hover:text-slate-100'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">{role.name}</p>
                                        {role.description && (
                                            <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>
                                        )}
                                    </div>
                                    {selectedRole?.id === role.id && (
                                        <ChevronRight size={16} className="text-primary" />
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right Column - Templates for Selected Role */}
                <div className="col-span-8 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden flex flex-col">
                    {!selectedRole ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center">
                                <AlertCircle size={32} className="text-slate-600 mx-auto mb-3" />
                                <p className="text-slate-500">Selecciona un rol para ver sus templates</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Header */}
                            <div className="px-5 py-4 border-b border-slate-800">
                                <h2 className="font-semibold text-slate-200">
                                    Templates para: <span className="text-primary">{selectedRole.name}</span>
                                </h2>
                                <p className="text-xs text-slate-500 mt-1">
                                    Los agentes de este rol verán estos templates al iniciar una conversación
                                </p>
                            </div>

                            {/* Assigned Templates */}
                            <div className="flex-1 overflow-y-auto p-5 space-y-4">
                                <div>
                                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                                        Templates Asignados ({roleTemplates.length})
                                    </h3>
                                    {roleTemplates.length === 0 ? (
                                        <div className="p-6 bg-slate-800/50 border border-slate-700 rounded-xl text-center">
                                            <MessageSquare size={24} className="text-slate-600 mx-auto mb-2" />
                                            <p className="text-sm text-slate-500">No hay templates asignados a este rol</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {roleTemplates.map(template => {
                                                const Icon = LucideIcons[template.icon_name] || MessageSquare;
                                                return (
                                                    <div
                                                        key={template.id}
                                                        className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 hover:border-slate-600 transition-colors"
                                                    >
                                                        <div className="p-2 bg-emerald-500/10 rounded-lg">
                                                            <Icon size={18} className="text-emerald-500" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-slate-200">{template.name}</p>
                                                            {template.description && (
                                                                <p className="text-xs text-slate-500 mt-0.5">{template.description}</p>
                                                            )}
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <code className="text-[10px] text-slate-600 bg-slate-900 px-2 py-0.5 rounded font-mono">
                                                                    {template.meta_template_name || template.id.substring(0, 8) + '...'}
                                                                </code>
                                                                {template.language_code && (
                                                                    <span className="text-[10px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded">
                                                                        {template.language_code}
                                                                    </span>
                                                                )}
                                                                <button
                                                                    onClick={() => copyToClipboard(template.id)}
                                                                    className="text-slate-600 hover:text-slate-400 transition-colors"
                                                                    title="Copiar ID completo"
                                                                >
                                                                    <Copy size={12} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => removeTemplate(template.id)}
                                                            disabled={busy}
                                                            className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                            title="Desasignar template"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Assign Template Selector */}
                                {availableTemplates.length > 0 && (
                                    <div className="pt-4 border-t border-slate-800">
                                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                                            Asignar Template
                                        </h3>
                                        <select
                                            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-primary/60 transition-colors"
                                            value=""
                                            onChange={e => e.target.value && assignTemplate(e.target.value)}
                                            disabled={busy}
                                        >
                                            <option value="">+ Selecciona un template para asignar...</option>
                                            {availableTemplates.map(t => (
                                                <option key={t.id} value={t.id}>
                                                    {t.name} {t.description ? `- ${t.description}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplatesByRole;
